import * as React from 'react';

import { Cell } from '../../components/NewbuildingsComparePage/parts/Cell';
import { RoomStatsToggleButton } from '../../components/NewbuildingsComparePage/parts/RoomStatsToggleButton';
import { INewbuildingsCompareDataRow, TNewbuildingsCompareViewRow } from '../../components/NewbuildingsComparePage';
import {
  INewbuildingSummaryRow,
  INewbuildingsCompareBlock,
  TNewbuildingsCompareRow,
} from '../../types/newbuildingsCompare';
import { blockTypeDeterminer } from './blockTypeDeterminer';

const VISIBLE_ROOM_STATS = 3;

export function isNewbuildingSummaryRow(row: TNewbuildingsCompareRow): row is INewbuildingSummaryRow {
  return row.type === 'newbuildingSummary';
}

/**
 * Максимальная длина списка среди ячеек строки для типов с вертикальным списком значений.
 * Используется, чтобы у высоких строк (несколько элементов) заголовок прижимался к верху,
 * а у одноэлементных — оставался по центру.
 */
function getRowMaxListLength(row: TNewbuildingsCompareRow): number {
  if (row.type === 'infrastructure') {
    return Math.max(0, ...row.cells.map(cell => cell.data?.infrastructures?.length ?? 0));
  }

  if (row.type === 'textList') {
    return Math.max(0, ...row.cells.map(cell => cell.data?.items?.length ?? 0));
  }

  if (row.type === 'transportPointExtended') {
    return Math.max(0, ...row.cells.map(cell => cell.data?.transportPoints?.length ?? 0));
  }

  return 0;
}

interface IBuildViewRowsParams {
  blocks: INewbuildingsCompareBlock[];
  isRoomStatsExpanded: boolean;
  onToggleRoomStats: () => void;
}

interface IBuildViewRowsResult {
  rows: TNewbuildingsCompareViewRow[];
  summaryRow: INewbuildingSummaryRow | undefined;
}

/**
 * Разворачивает блоки сравнения ЖК в плоский список строк для рендера таблицы.
 * Используется и десктопной, и мобильной страницами сравнения.
 */
export function buildNewbuildingsCompareViewRows({
  blocks,
  isRoomStatsExpanded,
  onToggleRoomStats,
}: IBuildViewRowsParams): IBuildViewRowsResult {
  const summaryRow = blocks.flatMap(block => block.rows).find(isNewbuildingSummaryRow);

  const rows = blocks.flatMap<TNewbuildingsCompareViewRow>((block, blockIndex) => {
    const blockType = blockTypeDeterminer(block);
    const nonSummaryRows = block.rows.filter(row => !isNewbuildingSummaryRow(row));
    const totalRoomStats = nonSummaryRows.filter(row => row.type === 'roomStats').length;

    let roomStatsSeen = 0;
    const dataRows: TNewbuildingsCompareViewRow[] = [];
    // Строка «Застройщик» и идущая сразу за ней безымянная строка-продолжение объединяются
    // в группу (общий hover). Держим ссылку на застройщика, пока не встретим его продолжение.
    let pendingDeveloperRow: INewbuildingsCompareDataRow | null = null;

    nonSummaryRows.forEach((row, rowIndex) => {
      if (row.type === 'roomStats') {
        const isHidden = !isRoomStatsExpanded && roomStatsSeen >= VISIBLE_ROOM_STATS;
        roomStatsSeen += 1;

        if (isHidden) {
          return;
        }
      }

      const dataRow: INewbuildingsCompareDataRow = {
        // Заголовок прижимаем к верху у строк-списков и у застройщика (на уровне аватара).
        alignTitleTop: row.type === 'developer' || getRowMaxListLength(row) > 1,
        cells: row.cells.map((cell, index) => <Cell cell={cell} blockType={blockType} key={index} type={row.type} />),
        key: `${blockIndex}-${row.type}-${row.title ?? rowIndex}`,
        kind: 'data',
        title: row.title,
      };

      if (pendingDeveloperRow && !row.title) {
        const groupId = `dev-group-${blockIndex}-${rowIndex}`;
        pendingDeveloperRow.groupId = groupId;
        dataRow.groupId = groupId;
      }
      pendingDeveloperRow = row.type === 'developer' ? dataRow : null;

      dataRows.push(dataRow);
    });

    if (blockType === 'flats' && totalRoomStats > VISIBLE_ROOM_STATS) {
      dataRows.push({
        content: <RoomStatsToggleButton expanded={isRoomStatsExpanded} onClick={onToggleRoomStats} />,
        key: `${blockIndex}-roomstats-toggle`,
        kind: 'action',
      });
    }

    if (block.title) {
      return [{ key: `block-${blockIndex}`, kind: 'block', title: block.title }, ...dataRows];
    }

    return dataRows;
  });

  return { rows, summaryRow };
}
